import { ColDef, ValueGetterParams } from '@ag-grid-community/core'
import { NgGridComponents } from '@northfork/ag-grid-angular'
import {
  ColumnTypeOverride,
  ColumnTypeOverrideService,
  DEFAULT_COLUMN_TYPE_OVERRIDE,
} from '@helios/filter-modal'
import {
  ColumnCatalogEntry,
  HELIOS_GROUP,
  SourceDefinition,
} from '../models/source-definition.model'
import { MatchingService } from '../services/matching.service'
import { AGG_INFO_FILTER } from './filters/agg-info-column-filter.component'
import {
  buildComputedColumns,
  COMPUTED_COLUMN_ALIASES,
  heliosColumnOverride,
} from '../registry/computed-columns'
import {
  CALC_FIELD_PREFIX,
  CalcField,
  calcFieldOf,
  calcUnitValue,
} from '../registry/unit-difference'
import {
  formatBoolean,
  formatCell,
  formatDate,
  formatDateTime,
  moneyComparator,
  moneyValueFormatter,
  numericValueOf,
  parseCellDate,
} from './renderers/renderers'

const RIGHT = 'rightAligned'

const AGGREGATE_INFO_GROUP = 'Aggregate Info'

/**
 * The catalog's own ordinary (POS_DATA) column exposing the raw discovery blob verbatim — distinct
 * from the per-key AGGREGATE_INFO columns discovered FROM it, which read their own value out of the
 * parsed `aggregateInfo` map instead. This is the one place `row.fields['aggregateInformation']` (the
 * brace-stripped JSON fragment kept for exactly this column — see `row.adapter.ts`) is ever rendered.
 */
const AGGREGATE_INFORMATION_FIELD = 'aggregateInformation'

/**
 * De-quote and space out the brace-stripped `"Key":"Value","Key2":"Value2"` fragment for display, the
 * same transform 1.0's External Position Detail panel applies to the identical field
 * (`external-position-detail-panel.component.ts:128-130`) — this column is that panel's schema turned
 * into a grid column, so it reads the same way here. Left un-parsed (rather than rebuilt from the
 * already-parsed `aggregateInfo` map) so a key this UI does not otherwise surface still displays.
 */
const formatAggregateInformationBlob = (value: unknown): string => {
  if (typeof value !== 'string' || value === '') return formatCell(value)
  return value.replace(/"/g, '').replace(/,/g, ', ').replace(/:/g, ': ')
}

/**
 * Options controlling how a source's ColDefs are built. All optional, so the pure-rendering call
 * sites (and the existing specs) keep working unchanged; each one exists because the *server-side*
 * grids need a decision the pure catalog data cannot make.
 */
export interface ColDefBuildOptions {
  /**
   * The {@link ColumnTypeOverrideService} scope AGGREGATE_INFO columns write their chosen comparison
   * type under. When absent, no type-override control is offered and those columns fall back to a
   * plain text filter — which is what the client-side Clearwater grid wants.
   */
  typeOverrideScope?: string
  /** The resolved override for a field, if the user has chosen one. */
  overrideFor?: (field: string) => ColumnTypeOverride | undefined
  /** The live override store, handed to the custom filter component through `filterParams`. */
  columnTypeOverrideService?: ColumnTypeOverrideService
}

/**
 * The only columns whose value domain is genuinely CLOSED, and therefore the only ones that may be
 * presented as a set filter on a server-side grid.
 *
 * Everything else gets a typed scalar filter. That is not a preference: on the server-side row model
 * a set filter must be handed its values, and the only other place to get them is the rows currently
 * loaded — which would present the first block's distinct values as though they were the column's
 * whole domain. A user who filters to what the list shows would silently exclude rows that exist
 * beyond the loaded blocks, and would have no way to tell. `/flex-positions/setValues` is the house
 * convention that would supply real domains (five other panels ship one); Flex Positions has no such
 * endpoint yet, which is a named backend gap rather than something to paper over here.
 *
 * FALLBACK ONLY, used when a catalog entry carries no `allowedValues` (a backend predating that
 * field). Once the catalog sends `allowedValues`, `domainFilterFor` prefers it over this — which is
 * how a value the backend adds later (`IGNORED` did, for `matchStatus`, via PVAL-26415) becomes
 * usable here without a UI release. Keep this list at whatever the OLDEST supported backend sends,
 * not the newest.
 */
const CLOSED_DOMAIN_VALUES: Record<string, readonly string[]> = {
  matchStatus: ['MATCHED', 'UNMATCHED'],
  recordSource: ['ENFUSION', 'PROMETHEUS'],
}

/** Boolean columns are a closed domain of two; ag-Grid's set filter renders these labels. */
const BOOLEAN_VALUES: readonly string[] = ['True', 'False']

/**
 * The chosen-type indicator appended to an AGGREGATE_INFO column's header, so the current type is
 * visible without opening the filter popover. Plain Unicode, not the picker's `mdi` icon class: the
 * header is a plain-text string (ag-Grid escapes `headerName`, and this version has no partial header
 * override to render an icon element instead), so `#`/`A` carry over as-is but the picker's mdi
 * calendar icon cannot — this is a text-only stand-in for the same Date glyph.
 */
const AGG_INFO_TYPE_GLYPH: Record<ColumnTypeOverride, string> = {
  text: 'A',
  number: '#',
  date: '📅',
}

const closedDomainSetFilter = (
  base: ColDef,
  values: readonly string[],
  filterValueGetter?: ColDef['filterValueGetter'],
): ColDef => ({
  ...base,
  filter: 'agSetColumnFilter',
  filterParams: { values: [...values] },
  filterValueGetter,
})

/**
 * Sort on the same instant the cell displays. Uses the shared parser so a date-only value and a
 * timestamped one order against each other by calendar date rather than by a UTC-offset artefact.
 */
const dateComparator = (a: unknown, b: unknown): number => {
  const at = parseCellDate(a)?.getTime() ?? 0
  const bt = parseCellDate(b)?.getTime() ?? 0
  return at - bt
}

/**
 * Read a catalog field off the row. Three kinds of field resolve differently: Aggregate Info fields
 * come from the discovered AggregateInformation blob (keyed by the raw `field`, since the API's
 * colSource is what marks them now — the old `agg:` scheme is gone), `calc:` fields are derived from
 * the match graph, and everything else is a plain lookup in `row.fields`.
 *
 * The agg-info branch falls back to `row.fields` because a PROJECTED agg key is HOISTED to the top
 * level of the response row rather than left inside the blob (contract: "a named pos_data key is
 * hoisted to the top level"), so it lands in `fields`. Both shapes therefore resolve, which is what
 * lets the same ColDef serve a projected request and the whole-blob one.
 */
const fieldGetter =
  (entry: ColumnCatalogEntry, matching: MatchingService) =>
  (params: ValueGetterParams): unknown => {
    const data = params.data
    if (!data) return null

    if (entry.colSource === AGGREGATE_INFO_GROUP) {
      return (
        data.aggregateInfo?.[entry.field] ?? data.fields[entry.field] ?? null
      )
    }

    const calcField = calcFieldOf(entry.field)
    if (calcField) return calcUnitValue(calcField, data, matching)

    return data.fields[entry.field] ?? null
  }

/**
 * Build the type-driven ColDef for a catalog column, then merge the Helios override (if the UI owns
 * this column's rendering) on top so the catalog still supplies colId, header and visibility while
 * the renderer / value accessor stay UI-owned.
 */
const catalogColDef = (
  entry: ColumnCatalogEntry,
  matching: MatchingService,
  options: ColDefBuildOptions = {},
): ColDef => {
  const getter = fieldGetter(entry, matching)
  const base: ColDef = {
    colId: entry.field,
    // The Filters modal's shared column-filter-utils reads `field`, not `colId`, to identify a
    // column — without this every catalog column is silently dropped from the Filters modal (no
    // error, just an empty "Select a column" list), because `valueGetter` above means `field` would
    // otherwise never be set at all.
    field: entry.field,
    headerName: entry.headerName,
    // Duplicate labels across sources are expected and are distinguishable ONLY by colSource, so the
    // header carries it too — as a tooltip rather than inline text, so a column whose label is
    // already unique is not widened for nothing.
    headerTooltip: entry.headerTooltip,
    hide: entry.hide,
    width: entry.width,
    valueGetter: getter,
  }

  let typed = applyCellType(base, entry, getter, options)
  if (entry.field === AGGREGATE_INFORMATION_FIELD) {
    typed = {
      ...typed,
      valueFormatter: params => formatAggregateInformationBlob(params.value),
    }
  }

  // A Helios-group catalog column the UI owns the presentation of: merge the override, which for the
  // match counts also clears the type-driven RIGHT alignment + formatter (Decision 5).
  const override =
    entry.colSource === HELIOS_GROUP
      ? heliosColumnOverride(entry.field, matching)
      : undefined
  return override ? { ...typed, ...override } : typed
}

/**
 * Apply the cell-type-driven presentation onto a base ColDef.
 *
 * The FILTER choice is the part that changed with PVAL-26423, and it is now driven by whether the
 * column's value domain is closed rather than by how its cells render:
 *
 *  - an AGGREGATE_INFO column gets the custom filter that carries the type picker (its catalog type is
 *    a placeholder, and it has no closed domain to list);
 *  - `matchStatus` / `recordSource` / BOOL get a set filter over their real, enumerable domain;
 *  - DATE / DATETIME get a DATE filter, not the set filter they used to get — a set filter over dates
 *    would have sent formatted display strings as the predicate values;
 *  - everything else gets a text filter.
 *
 * The scalar filters are NORTHFORK's (`NgGridComponents.*`), not ag-Grid's own `agTextColumnFilter` /
 * `agDateColumnFilter`. That is load-bearing on two counts, both of which are silent failures rather
 * than errors:
 *
 *  1. `@helios/server-side-util`'s converters read Northfork's `ScalerFilterModel` — `filter` /
 *     `filterTo` — whereas ag-Grid's native date filter emits `dateFrom` / `dateTo`. A native date
 *     filter therefore converts to `null` and `convertFilters` DROPS the entry, so the predicate never
 *     reaches /query and the grid shows unfiltered rows, looking exactly like "no filter applied".
 *  2. `filter-modal`'s `column-filter-utils.getFilterType` matches on the Northfork ids, so a column
 *     using an ag-Grid native id silently disappears from the Filters modal's column list.
 */
const applyCellType = (
  base: ColDef,
  entry: ColumnCatalogEntry,
  getter: (params: ValueGetterParams) => unknown,
  options: ColDefBuildOptions = {},
): ColDef => {
  const domainFilter = domainFilterFor(base, entry, getter, options)
  if (domainFilter) return domainFilter

  switch (entry.cellType) {
    case 'money':
      return moneyColDef(base, getter)
    case 'number':
      // The API has no MONEY type: a NUM column is right-aligned and, at runtime, upgraded to money
      // presentation whenever its value carries a currency (isMoney) so the money status bar and
      // cell-range sum work. Plain numbers fall through the money formatter untouched.
      return {
        ...moneyColDef(base, getter),
        type: RIGHT,
      }
    case 'date':
      return {
        ...base,
        valueFormatter: params => formatDate(params.value),
        comparator: dateComparator,
        filter: NgGridComponents.dateFilter,
      }
    case 'datetime':
      return {
        ...base,
        valueFormatter: params => formatDateTime(params.value),
        comparator: dateComparator,
        filter: NgGridComponents.dateTimeFilter,
      }
    case 'boolean':
      return closedDomainSetFilter(
        {
          ...base,
          valueFormatter: params => formatBoolean(params.value),
        },
        BOOLEAN_VALUES,
        params => formatBoolean(getter(params)),
      )
    default:
      return {
        ...base,
        filter: NgGridComponents.textFilter,
        valueFormatter: params => formatCell(params.value),
        filterValueGetter: params => formatCell(getter(params)),
      }
  }
}

/**
 * The filter for a column whose DOMAIN — not its rendering — dictates the control: an untyped
 * agg-info column, or a closed-domain column.
 *
 * Returns `undefined` when none applies, so `applyCellType` falls through to its type-driven branches.
 *
 * `matchStatus` gets NO special case: it is a closed domain like any other, and it keeps a real
 * `agSetColumnFilter`. The toolbar status chips are a second face on that same filter rather than a
 * competing one (see match-status-chips.ts), so suppressing the header filter would remove the surface
 * the chips synchronise with.
 */
const domainFilterFor = (
  base: ColDef,
  entry: ColumnCatalogEntry,
  getter: (params: ValueGetterParams) => unknown,
  options: ColDefBuildOptions,
): ColDef | undefined => {
  if (entry.storage === 'AGG_INFO' && options.typeOverrideScope) {
    const type =
      options.overrideFor?.(entry.field) ?? DEFAULT_COLUMN_TYPE_OVERRIDE
    return {
      ...base,
      // A plain-text suffix, not a custom header component: this ag-Grid version has no partial
      // header override, so a custom headerComponent would mean reimplementing the sort/menu/filter
      // icons from scratch to get one small badge — too much risk for what this needs. headerName is
      // rebuilt from the CURRENT type every time the ColDefs are (setColumnDefsPreservingState already
      // runs on every override change), so the glyph stays live without any header-specific wiring.
      headerName: `${base.headerName} ${AGG_INFO_TYPE_GLYPH[type]}`,
      valueFormatter: params => formatCell(params.value),
      filter: AGG_INFO_FILTER,
      filterParams: {
        // Read back by the component AND by the filter modal's column-filter-utils, which is how one
        // chosen type drives both surfaces.
        columnTypeOverride: { scope: options.typeOverrideScope, type },
        columnTypeOverrideService: options.columnTypeOverrideService,
      },
    }
  }

  // The catalog's own allowedValues is authoritative when present — it is what lets a value the
  // backend adds later (IGNORED did, for matchStatus) become usable here without a UI release.
  // CLOSED_DOMAIN_VALUES is the fallback for a catalog that predates the field. `?.length` rather than
  // `??`: an empty array is not nullish, so `??` would let a genuinely empty `allowedValues` through
  // as-is and build a values-less, unusable set filter instead of falling back.
  const values = entry.allowedValues?.length
    ? entry.allowedValues
    : CLOSED_DOMAIN_VALUES[entry.field]
  if (values) {
    return closedDomainSetFilter(
      { ...base, valueFormatter: params => formatCell(params.value) },
      values,
      params => formatCell(getter(params)),
    )
  }

  return undefined
}

/** Money presentation: right-aligned, currency-aware formatter / comparator, numeric filter. */
const moneyColDef = (
  base: ColDef,
  getter: (params: ValueGetterParams) => unknown,
): ColDef => ({
  ...base,
  type: RIGHT,
  valueFormatter: moneyValueFormatter,
  comparator: moneyComparator,
  filter: NgGridComponents.numberFilter,
  filterValueGetter: params => {
    const value = params.data ? getter(params) : null
    return numericValueOf(value)
  },
})

/** The four Clearwater `calc:` unit-difference columns, as UI-owned External-group entries. */
const CALC_COLUMNS: ReadonlyArray<[CalcField, string, number]> = [
  ['custUnits', 'Custody Units', 124],
  ['tradUnits', 'Trading Units', 124],
  ['custUnitsDiff', 'Custody Units Difference', 170],
  ['tradUnitsDiff', 'Trading Units Difference', 170],
]

const buildCalcColumns = (matching: MatchingService): ColDef[] =>
  CALC_COLUMNS.map(([field, headerName, width]) =>
    catalogColDef(
      {
        field: `${CALC_FIELD_PREFIX}${field}`,
        headerName,
        colSource: 'External',
        cellType: 'number',
        width,
      },
      matching,
    ),
  )

/**
 * The computed colIds a present catalog covers, so the corresponding survivor is not spliced in a
 * second time.
 */
const aliasedColIds = (catalogColumns: ColumnCatalogEntry[]): Set<string> => {
  const aliased = new Set<string>()
  catalogColumns.forEach(entry => {
    const colId = COMPUTED_COLUMN_ALIASES[entry.field]
    if (colId) aliased.add(colId)
  })
  return aliased
}

/**
 * Assemble the full ordered ColDef list for a source from its resolved catalog:
 *  - the catalog columns (each Helios one merged with its UI override), in server order;
 *  - the surviving computed Helios columns the catalog did NOT return, spliced in AFTER the last
 *    catalog HELIOS-group column so the Helios block stays contiguous (never computed-first);
 *  - the Clearwater `calc:` unit-difference columns for the cw source (UI-owned, no backend);
 *  - any runtime-discovered Aggregate Info columns.
 *
 * When the catalog is empty (no account focused, a fetch error, or a genuinely empty source) the
 * computed columns are the whole list — the synchronous seed the pipeline starts from.
 */
export function buildColDefs(
  source: SourceDefinition,
  matching: MatchingService,
  catalogColumns: ColumnCatalogEntry[],
  aggregateColumns: ColumnCatalogEntry[],
  options: ColDefBuildOptions = {},
): ColDef[] {
  const catalogDefs = catalogColumns.map(entry =>
    catalogColDef(entry, matching, options),
  )
  const aliased = aliasedColIds(catalogColumns)

  const survivors = buildComputedColumns(source, matching).filter(
    col => !aliased.has(col.colId as string),
  )

  // Splice survivors after the last HELIOS-group catalog column so the Helios block is contiguous.
  const heliosFields = new Set(
    catalogColumns
      .filter(entry => entry.colSource === HELIOS_GROUP)
      .map(entry => entry.field),
  )
  let lastHeliosIndex = -1
  catalogDefs.forEach((col, index) => {
    if (heliosFields.has(col.colId as string)) lastHeliosIndex = index
  })

  const merged: ColDef[] =
    lastHeliosIndex >= 0
      ? [
          ...catalogDefs.slice(0, lastHeliosIndex + 1),
          ...survivors,
          ...catalogDefs.slice(lastHeliosIndex + 1),
        ]
      : // No catalog HELIOS columns: computed columns lead, then the catalog (the seed shape).
        [...survivors, ...catalogDefs]

  const calcDefs = source.hasUnitDifferenceColumns
    ? buildCalcColumns(matching)
    : []

  return withFieldIdentity(
    dedupeByColId([
      ...merged,
      ...calcDefs,
      ...aggregateColumns.map(entry => catalogColDef(entry, matching, options)),
    ]),
  )
}

/**
 * Backfill `field` from `colId` on every column that has no `field` of its own.
 *
 * Anything that addresses a single CELL does so by `colDef.field` — `HighlightingService`
 * (`getIdentifiers` reads `column.getColDef().field`) and the shared filter-on-cell helper both do.
 * This panel identifies columns by `colId` alone: values come from a `valueGetter` reading
 * `row.fields[...]` rather than from the row root, so `field` was never set. With it absent every
 * column reports the empty string as its identity, so a cell highlight is recorded under `''` and
 * then matched by every cell in the row — highlighting the whole row instead of one cell.
 *
 * Inert for value retrieval: every column this factory emits declares a `valueGetter`, and ag-Grid
 * prefers a `valueGetter` over `field`. That matters here more than it looks — several computed
 * colIds (`breaks`, `comments`, `source`) collide with real {@link WorkbenchRow} properties, so
 * without the getters this would start reading those objects as cell values.
 */
function withFieldIdentity(columns: ColDef[]): ColDef[] {
  return columns.map(column =>
    column.field == null && column.colId != null
      ? { ...column, field: column.colId }
      : column,
  )
}

/**
 * Keep the first column claiming each colId and drop later repeats.
 *
 * ag-Grid tolerates a duplicate colId by silently renaming the second one (`Custodian` ->
 * `Custodian_1`). That renamed id matches nothing in {@link colSourceMap}, so the column falls out
 * of its section and into the Column Manager's ungrouped bucket — the whole column also being a
 * duplicate of one already on screen. Dropping repeats here means grouping cannot drift from the
 * columns however the catalog, computed, calc and Aggregate Info lists overlap.
 */
function dedupeByColId(columns: ColDef[]): ColDef[] {
  const claimed = new Set<string>()
  return columns.filter(column => {
    const colId = column.colId
    if (!colId) return true
    if (claimed.has(colId)) return false
    claimed.add(colId)
    return true
  })
}

/**
 * Expose the colSource group for each colId so the Column Manager can group columns. Computed
 * (survivor) columns are attributed to the Helios group; catalog, calc: and Aggregate Info columns
 * carry their own.
 */
export function colSourceMap(
  source: SourceDefinition,
  matching: MatchingService,
  catalogColumns: ColumnCatalogEntry[],
  aggregateColumns: ColumnCatalogEntry[],
): Record<string, string> {
  const map: Record<string, string> = {}

  const aliased = aliasedColIds(catalogColumns)
  buildComputedColumns(source, matching)
    .filter(col => !aliased.has(col.colId as string))
    .forEach(col => {
      map[col.colId as string] = HELIOS_GROUP
    })

  catalogColumns.forEach(entry => {
    map[entry.field] = entry.colSource
  })

  if (source.hasUnitDifferenceColumns) {
    CALC_COLUMNS.forEach(([field]) => {
      map[`${CALC_FIELD_PREFIX}${field}`] = 'External'
    })
  }

  aggregateColumns.forEach(entry => {
    map[entry.field] = entry.colSource
  })

  return map
}
